/**
 * End-to-End Encryption Library
 *
 * Client-side encryption using NaCl (TweetNaCl implementation)
 * Compatible with the Go nacl/box implementation on the backend
 *
 * Security Properties:
 * - Private keys NEVER leave the client
 * - Public-key cryptography (Curve25519)
 * - Ephemeral keys for forward secrecy
 * - Authenticated encryption (prevent tampering)
 */

import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';

// Use native browser APIs for UTF8 encoding/decoding (more reliable than tweetnacl-util)
function encodeUTF8(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

function decodeUTF8(arr: Uint8Array): string {
  return new TextDecoder().decode(arr);
}

export interface KeyPair {
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}

export interface SerializedKeyPair {
  publicKey: string; // hex encoded
  privateKey: string; // hex encoded
}

/**
 * Generate a new Curve25519 keypair for E2E encryption
 * Private key should be stored securely client-side only
 */
export function generateKeyPair(): KeyPair {
  const keyPair = nacl.box.keyPair();

  console.log('Generated keypair types:', {
    publicKey: keyPair.publicKey?.constructor?.name,
    publicKeyLength: keyPair.publicKey?.length,
    secretKey: keyPair.secretKey?.constructor?.name,
    secretKeyLength: keyPair.secretKey?.length,
  });

  return {
    publicKey: keyPair.publicKey,
    privateKey: keyPair.secretKey,
  };
}

/**
 * Encrypt a message for a specific recipient
 * Uses ephemeral keypair for forward secrecy
 *
 * Format: [ephemeral_public_key (32 bytes)][nonce (24 bytes)][ciphertext]
 */
export function encryptMessage(plaintext: string, recipientPublicKey: Uint8Array): string {
  if (!plaintext) {
    throw new Error('Plaintext cannot be empty');
  }

  if (!recipientPublicKey || recipientPublicKey.length !== 32) {
    throw new Error('Invalid recipient public key');
  }

  // Generate ephemeral keypair for forward secrecy
  const ephemeralKeyPair = nacl.box.keyPair();

  // Generate random nonce
  const nonce = nacl.randomBytes(24);

  // Convert plaintext to bytes
  const messageBytes = encodeUTF8(plaintext);

  // Debug: Check types before calling nacl.box
  console.log('nacl.box arguments:', {
    messageBytes: messageBytes?.constructor?.name,
    messageBytesLength: messageBytes?.length,
    nonce: nonce?.constructor?.name,
    nonceLength: nonce?.length,
    recipientPublicKey: recipientPublicKey?.constructor?.name,
    recipientPublicKeyLength: recipientPublicKey?.length,
    ephemeralSecretKey: ephemeralKeyPair.secretKey?.constructor?.name,
    ephemeralSecretKeyLength: ephemeralKeyPair.secretKey?.length,
  });

  // Encrypt using ephemeral private key and recipient's public key
  const ciphertext = nacl.box(messageBytes, nonce, recipientPublicKey, ephemeralKeyPair.secretKey);

  if (!ciphertext) {
    throw new Error('Encryption failed: nacl.box returned null');
  }

  // Combine: [ephemeral_public_key][nonce + ciphertext]
  const message = new Uint8Array(32 + nonce.length + ciphertext.length);
  message.set(ephemeralKeyPair.publicKey, 0);
  message.set(nonce, 32);
  message.set(ciphertext, 32 + nonce.length);

  // Encode to base64 for transmission/storage
  return encodeBase64(message);
}

/**
 * Decrypt a message using own private key
 * This should ONLY happen client-side
 */
export function decryptMessage(
  encrypted: string,
  recipientPrivateKey: Uint8Array
): string {
  if (!encrypted) {
    throw new Error('Encrypted text cannot be empty');
  }

  if (!recipientPrivateKey || recipientPrivateKey.length !== 32) {
    throw new Error('Invalid recipient private key');
  }

  // Decode from base64
  const message = decodeBase64(encrypted);

  if (message.length < 32 + 24) {
    throw new Error('Invalid encrypted message: too short');
  }

  // Extract ephemeral public key (first 32 bytes)
  const ephemeralPublicKey = message.slice(0, 32);

  // Extract nonce (next 24 bytes)
  const nonce = message.slice(32, 32 + 24);

  // Extract ciphertext (remaining bytes)
  const ciphertext = message.slice(32 + 24);

  // Decrypt using recipient's private key and ephemeral public key
  const decrypted = nacl.box.open(ciphertext, nonce, ephemeralPublicKey, recipientPrivateKey);

  if (!decrypted) {
    throw new Error('Failed to decrypt message: authentication failed');
  }

  // Convert bytes back to string
  return decodeUTF8(decrypted);
}

/**
 * Encrypt message for multiple recipients (group chat)
 */
export function encryptForMultipleRecipients(
  plaintext: string,
  recipientPublicKeys: Uint8Array[]
): string[] {
  if (recipientPublicKeys.length === 0) {
    throw new Error('At least one recipient required');
  }

  return recipientPublicKeys.map((publicKey) => encryptMessage(plaintext, publicKey));
}

/**
 * Convert public key to hex string for display/transmission
 */
export function publicKeyToString(publicKey: Uint8Array): string {
  return Array.from(publicKey)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Convert hex string back to public key
 */
export function publicKeyFromString(keyStr: string): Uint8Array {
  if (keyStr.length !== 64) {
    throw new Error(`Invalid key length: expected 64 hex chars, got ${keyStr.length}`);
  }

  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    bytes[i] = parseInt(keyStr.slice(i * 2, i * 2 + 2), 16);
  }

  return bytes;
}

/**
 * Serialize keypair for storage (WARNING: Store private key securely!)
 */
export function serializeKeyPair(keyPair: KeyPair): SerializedKeyPair {
  return {
    publicKey: publicKeyToString(keyPair.publicKey),
    privateKey: publicKeyToString(keyPair.privateKey),
  };
}

/**
 * Deserialize keypair from storage
 */
export function deserializeKeyPair(serialized: SerializedKeyPair): KeyPair {
  return {
    publicKey: publicKeyFromString(serialized.publicKey),
    privateKey: publicKeyFromString(serialized.privateKey),
  };
}

/**
 * Verify that a keypair is valid by encrypting and decrypting a test message
 */
export function verifyKeyPair(keyPair: KeyPair): boolean {
  const testMessage = 'test_message_for_keypair_verification';

  try {
    // Validate keypair structure
    if (!keyPair || !keyPair.publicKey || !keyPair.privateKey) {
      console.error('Invalid keypair structure:', keyPair);
      return false;
    }

    if (keyPair.publicKey.length !== 32 || keyPair.privateKey.length !== 32) {
      console.error('Invalid key lengths:', {
        publicKeyLength: keyPair.publicKey.length,
        privateKeyLength: keyPair.privateKey.length,
      });
      return false;
    }

    console.log('Verifying keypair - encrypting test message...');
    const encrypted = encryptMessage(testMessage, keyPair.publicKey);
    console.log('Encrypted successfully, length:', encrypted.length);

    console.log('Decrypting test message...');
    const decrypted = decryptMessage(encrypted, keyPair.privateKey);
    console.log('Decrypted successfully:', decrypted);

    const isValid = decrypted === testMessage;
    console.log('Verification result:', isValid);

    return isValid;
  } catch (error) {
    console.error('Keypair verification failed:', error);
    return false;
  }
}

/**
 * Generate a secure device ID
 */
export function generateDeviceId(): string {
  const bytes = nacl.randomBytes(16);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
